// Where a chart's high and low labels go (TOM-445). The usual place is the
// value over a high and under a low, its clock time stacked beyond it. A short
// plot has no room for that stack: the old code clamped it back into the plot,
// onto the curve, the marks and the "now" dot (the watch bento's Tide drew
// "3:00 AM" on its low's curve and "2.4" under the now dot). So each label
// tries its usual place first, then one row beside its mark, value then time,
// to the right and then the left; then the same without the time. It takes the
// first place clear of the curve, every mark, the now dot and the labels
// already placed, and none at all when nothing is clear.

export interface Box {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

export interface Circle {
  cx: number;
  cy: number;
  r: number;
}

export type Anchor = 'start' | 'middle' | 'end';

export interface TextSpot {
  x: number;
  /** The baseline. */
  y: number;
  anchor: Anchor;
}

export interface ExtremeSpot {
  value: TextSpot;
  /**
   * The time's own spot when stacked. On one row (the value anchored at its
   * start or end) it is the value's, the time following in the same text.
   * Null without a time.
   */
  time: TextSpot | null;
}

/**
 * A label's ink: figures, capitals and "AM"/"PM" rise about 0.75 em above the
 * baseline and drop nothing below it, so the em box's descent is air that may
 * overhang the plot's edge.
 */
export function textBox(spot: TextSpot, width: number, sizePx: number): Box {
  const x0 =
    spot.anchor === 'start' ? spot.x : spot.anchor === 'end' ? spot.x - width : spot.x - width / 2;
  return { x0, x1: x0 + width, y0: spot.y - sizePx * 0.8, y1: spot.y + sizePx * 0.1 };
}

const overlaps = (a: Box, b: Box) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;

/** How far a curve's stroke and a circle's ring keep from a label. */
const CLEAR_PX = 1.5;

/** Whether `box` is inside `bounds` and clear of the curve, the circles and the boxes taken. */
export function clearOf(
  box: Box,
  bounds: Box,
  curveY: (x: number) => number | null,
  circles: readonly Circle[],
  taken: readonly Box[]
): boolean {
  if (box.x0 < bounds.x0 || box.x1 > bounds.x1 || box.y0 < bounds.y0 || box.y1 > bounds.y1) {
    return false;
  }
  for (const c of circles) {
    const nx = Math.max(box.x0, Math.min(c.cx, box.x1));
    const ny = Math.max(box.y0, Math.min(c.cy, box.y1));
    if (Math.hypot(c.cx - nx, c.cy - ny) < c.r + CLEAR_PX) return false;
  }
  if (taken.some((t) => overlaps(box, t))) return false;
  for (let x = box.x0; x <= box.x1; x += 1) {
    const cy = curveY(x);
    if (cy !== null && cy > box.y0 - CLEAR_PX && cy < box.y1 + CLEAR_PX) return false;
  }
  return true;
}

export interface ExtremeLabelInput {
  kind: 'max' | 'min';
  mark: { x: number; y: number };
  value: { width: number; sizePx: number };
  time: { width: number; sizePx: number } | null;
  bounds: Box;
  curveY: (x: number) => number | null;
  circles: readonly Circle[];
  taken: readonly Box[];
}

/** The gap between a mark's centre and a label beside it: its ring and air. */
const BESIDE_PX = 9;
/** The space between the value and its time on one row. */
const ROW_GAP_PX = 4;

/**
 * The first place clear for an extreme's labels, with the boxes it takes, or
 * null when no place is clear and the chart shows the mark alone.
 */
export function placeExtremeLabel(
  input: ExtremeLabelInput
): { spot: ExtremeSpot; boxes: Box[] } | null {
  const { kind, mark, value, bounds, curveY, circles, taken } = input;
  const up = kind === 'max';

  const stacked = (withTime: boolean): ExtremeSpot => {
    const vy = up ? mark.y - 9 : mark.y + 16;
    const middle = (width: number) =>
      Math.max(bounds.x0 + width / 2, Math.min(mark.x, bounds.x1 - width / 2));
    return {
      value: { x: middle(value.width), y: vy, anchor: 'middle' },
      time:
        withTime && input.time !== null
          ? { x: middle(input.time.width), y: up ? vy - 11 : vy + 11, anchor: 'middle' }
          : null,
    };
  };

  // One row, the time following the value in the same text: a high's sits
  // above its mark's centre, a low's below, so the curve, falling away from a
  // high and rising from a low, stays clear of it. On the left the row ends
  // beside the mark, whatever its drawn width.
  const beside = (side: 'right' | 'left', withTime: boolean): ExtremeSpot => {
    const y = up ? mark.y - 3 : mark.y + value.sizePx * 0.8 + 1;
    const spot: TextSpot =
      side === 'right'
        ? { x: mark.x + BESIDE_PX, y, anchor: 'start' }
        : { x: mark.x - BESIDE_PX, y, anchor: 'end' };
    return { value: spot, time: withTime && input.time !== null ? spot : null };
  };

  const boxesOf = (spot: ExtremeSpot): Box[] => {
    if (spot.value.anchor !== 'middle') {
      const row = value.width + (spot.time !== null ? ROW_GAP_PX + (input.time?.width ?? 0) : 0);
      return [textBox(spot.value, row, value.sizePx)];
    }
    return [
      textBox(spot.value, value.width, value.sizePx),
      ...(spot.time !== null && input.time !== null
        ? [textBox(spot.time, input.time.width, input.time.sizePx)]
        : []),
    ];
  };

  const tries = (withTime: boolean) => [
    stacked(withTime),
    beside('right', withTime),
    beside('left', withTime),
  ];
  const candidates = input.time !== null ? [...tries(true), ...tries(false)] : tries(false);
  for (const spot of candidates) {
    const boxes = boxesOf(spot);
    if (boxes.every((b) => clearOf(b, bounds, curveY, circles, taken))) return { spot, boxes };
  }
  return null;
}
